/**
 * JsonView — a small, dependency-free recursive renderer for arbitrary JSON.
 *
 * Used by MicroTransactionDetail to display a micro-transaction's raw_payload,
 * whose per-app shape is not known to the frontend. Objects and arrays collapse;
 * the root starts expanded so its top-level fields are visible, while nested
 * objects/arrays start collapsed. Colors use the app's semantic tokens so it
 * works in light, dark, and high-contrast themes.
 */

import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";
import { cn } from "@/shared/lib/utils";

type JsonViewProps = {
  value: unknown;
  name?: string;
  depth?: number;
};

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

export function JsonView({ value, name, depth = 0 }: JsonViewProps) {
  const [open, setOpen] = useState(depth === 0);

  const keyLabel =
    name !== undefined ? <span className="text-muted-foreground">{name}</span> : null;

  if (!Array.isArray(value) && !isPlainObject(value)) {
    return (
      <div className="flex gap-2 py-0.75 pl-4 leading-relaxed">
        {keyLabel && <span className="shrink-0">{keyLabel}:</span>}
        <Leaf value={value} />
      </div>
    );
  }

  const entries: Array<readonly [string, unknown]> = Array.isArray(value)
    ? value.map((v, i) => [String(i), v] as const)
    : Object.entries(value);
  const bracket = Array.isArray(value) ? `[${entries.length}]` : `{${entries.length}}`;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1 py-0.75 text-left text-foreground transition-colors hover:text-foreground/70"
      >
        <ChevronRight
          className={cn(
            "size-3 shrink-0 text-muted-foreground transition-transform duration-150",
            open && "rotate-90",
          )}
        />
        {keyLabel && <span>{keyLabel}:</span>}
        <span className="text-muted-foreground/70">{bracket}</span>
      </button>

      {open && entries.length > 0 && (
        <div className="ml-1.25 border-l pl-3">
          {entries.map(([k, v]) => (
            <JsonView key={k} name={k} value={v} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

function Leaf({ value }: { value: unknown }) {
  if (value === null || value === undefined) {
    return <span className="italic text-muted-foreground">null</span>;
  }
  if (typeof value === "string") {
    return <StringLeaf value={value} />;
  }
  if (typeof value === "number") {
    return <span className="tabular-nums text-primary">{value}</span>;
  }
  if (typeof value === "boolean") {
    return <span className="text-primary">{String(value)}</span>;
  }
  return <span className="break-all text-muted-foreground">{String(value)}</span>;
}

// Long string values are clamped so a single big payload can't blow up the
// row's height; click to reveal the full text.
const STRING_CLAMP_THRESHOLD = 160;

function StringLeaf({ value }: { value: string }) {
  const [expanded, setExpanded] = useState(false);

  if (value.length <= STRING_CLAMP_THRESHOLD) {
    return <span className="break-all whitespace-pre-wrap text-foreground">"{value}"</span>;
  }

  return (
    <div className="min-w-0 flex-1">
      <span
        className={cn(
          "block break-all whitespace-pre-wrap text-foreground",
          !expanded && "line-clamp-3",
        )}
      >
        "{value}"
      </span>
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="mt-1 inline-flex items-center gap-0.5 font-medium text-primary hover:underline"
      >
        {expanded ? "Show less" : "Show more"}
        <ChevronDown className={cn("size-3 transition-transform", expanded && "rotate-180")} />
      </button>
    </div>
  );
}
